import React from 'react'
import { Route, Routes, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'

import { useCanary } from '@bill/canary.react'

import { useAuth } from '@sputnik/auth'
import useDivvyUUID from '@sputnik/hooks/useDivvyUUID'
import ApplicationDeclined from '@sputnik/pages/ApplicationDeclined'
import GetApplicationStatuses from '@sputnik/pages/Dashboard/gql/GetApplicationStatuses.gql'
import GetApplicationProcessedCreditData from '@sputnik/pages/gql/GetApplicationProcessedCreditData.gql'
import GetDecisions from '@sputnik/pages/gql/GetDecisions.gql'
import SuccessFrozenCredit from '@sputnik/pages/SuccessFrozenCredit'
import SuccessPreApprovedOffers from '@sputnik/pages/SuccessPreApprovedOffers/SuccessPreApprovedOffers'
import SuccessWorkingOutDetails from '@sputnik/pages/SuccessWorkingOutDetails'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { matchEmail } from '@sputnik/utils/dataUtils'

import { FIXED_LINE, SECURED_LINE, VARIABLE_LINE } from './applicationProcessedUtils'
import { DecisionContext } from './DecisionsContext'
import SecuredCreditLineOfferAcceptance from './SecuredCreditLineOfferAcceptance'
import StandardCreditLineOfferAcceptance from './StandardCreditLineOfferAcceptance'
import StandardCreditLineOfferWithSelectorAcceptance from './StandardCreditLineOfferWithSelectorAcceptance'
import VariableCreditLineOfferAcceptance from './VariableCreditLineOfferAcceptance'

// TODO: delete this component and it's children after there are no more legacy (v2) apps
const ApplicationProcessed = () => {
  const { appId } = useParams()
  const { email } = useAuth()
  const [companyId] = useDivvyUUID()

  const isShowStandardLineWithSelector = useCanary('show-standard-line-with-selection')

  // TODO: make a new query that combines GetDecisions and GetApplicationProcessedCreditData
  const { data: decisionsData } = useQuery(GetDecisions, {
    fetchPolicy: 'network-only',
    skip: !companyId,
    variables: { companyId },
  })

  // TODO: replace GetApplicationStatuses with a new query that only calls creditApplications with not extra data inside
  // We are only checking how many apps there are so this query is fetching way too much data
  const { data: applicationData } = useQuery(GetApplicationStatuses)

  const { data: applicationProcessedCreditData } = useQuery(GetApplicationProcessedCreditData, {
    skip: !appId,
    variables: { creditApplicationId: appId },
  })
  const { financeInfo, authorizedSigner } = applicationProcessedCreditData?.creditApplication || {}
  const desiredCredit = financeInfo?.desiredCredit
  const authSignerEmail = authorizedSigner?.email
  const isLoggedInUserAuthorizedSigner = matchEmail(authSignerEmail, email)

  const decisions = decisionsData?.decisions || []
  const decisionId = decisions?.[0]?.id

  const standardLineOffer = decisions?.find((decision) => decision?.offerType === FIXED_LINE)
  const variableLineOffer = decisions?.find((decision) => decision?.offerType === VARIABLE_LINE)
  const securedLineOffer = decisions?.find((decision) => decision?.offerType === SECURED_LINE)

  const hasOnlyOneDecision = decisions?.length === 1
  const isStandardOfferOnly = !!(hasOnlyOneDecision && standardLineOffer)
  const isVariableOfferOnly = !!(hasOnlyOneDecision && variableLineOffer)
  const isSecuredOfferOnly = !!(hasOnlyOneDecision && securedLineOffer)

  // These variables below will either be updated or removed in following MR's for the new secured line flow
  const filteredStandardOffer = decisions.filter((decision) => decision.underwritingMethod !== 'auto_cash_underwriting')
  const applicationCount = applicationData?.creditApplications?.length
  const hasApplications = applicationCount > 0

  const approvedAmount = decisions?.[0]?.approvedAmountInCents

  const isCounterOffer = approvedAmount !== desiredCredit

  const hasSliderCriteria =
    filteredStandardOffer?.[0]?.experimentId && filteredStandardOffer?.[0]?.maxApprovedAmountInCents > 0
  const isOfferStandardLineIncrease = !!hasSliderCriteria && !!isShowStandardLineWithSelector
  const StandardCreditLineOfferAcceptanceComponent =
    isOfferStandardLineIncrease && !isCounterOffer
      ? StandardCreditLineOfferWithSelectorAcceptance
      : StandardCreditLineOfferAcceptance

  return (
    <>
      <DecisionContext.Provider
        value={{
          decisionId,
          decisions,
          desiredCredit,
          securedLineOffer,
          variableLineOffer,
          standardLineOffer,
          isLoggedInUserAuthorizedSigner,
          authSignerEmail,
        }}
      >
        <Routes>
          <Route
            element={<SuccessPreApprovedOffers />}
            path={PATHNAME_CONSTS.MULTI_CREDIT_LINE_OFFER_PATH}
          />
          <Route
            element={<StandardCreditLineOfferAcceptanceComponent />}
            path={PATHNAME_CONSTS.MULTI_OFFER_TO_STANDARD_OFFER_PATH}
          />
          <Route
            element={<VariableCreditLineOfferAcceptance />}
            path={PATHNAME_CONSTS.MULTI_OFFER_TO_VARIABLE_OFFER_PATH}
          />
          <Route
            element={<SecuredCreditLineOfferAcceptance />}
            path={PATHNAME_CONSTS.MULTI_OFFER_TO_SECURED_OFFER_PATH}
          />
          <Route
            element={
              <>
                {isStandardOfferOnly && <StandardCreditLineOfferAcceptanceComponent />}
                {isVariableOfferOnly && <VariableCreditLineOfferAcceptance />}
                {isSecuredOfferOnly && <SecuredCreditLineOfferAcceptance />}
              </>
            }
            path={PATHNAME_CONSTS.SINGLE_CREDIT_LINE_OFFER_PATH}
          />
          <Route
            element={<SuccessFrozenCredit />}
            path={`/${PATHNAME_CONSTS.FROZEN_CREDIT_PATH}`}
          />
          <Route
            element={<SuccessWorkingOutDetails />}
            path={`/${PATHNAME_CONSTS.MANUAL_REVIEW_PATH}`}
          />
          <Route
            element={<ApplicationDeclined hasApplications={hasApplications} />}
            path={PATHNAME_CONSTS.DECLINED_PATH}
          />
        </Routes>
      </DecisionContext.Provider>
    </>
  )
}

export default ApplicationProcessed
